import { Button, Col, Icon, Modal, Row, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Option = styled.button`
  width: 100%;
  text-align: left;
  border: 1.5px solid
    ${(p) =>
      p.state === 'correct'
        ? theme.GREEN
        : p.state === 'wrong'
          ? theme.SECONDARY
          : p.selected
            ? theme.SECONDARY
            : theme.GREY_20};
  background: ${(p) =>
    p.state === 'correct'
      ? `${theme.GREEN}12`
      : p.state === 'wrong'
        ? `${theme.SECONDARY}10`
        : p.selected
          ? `${theme.SECONDARY}08`
          : '#fff'};
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;
  font-family: CeraPro;
  font-size: 14px;
  color: ${theme.GREY_90};
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};
  display: flex;
  align-items: center;
  gap: 12px;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover {
    border-color: ${(p) => (p.disabled ? p.theme : theme.SECONDARY)};
  }
`;

const Radio = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid
    ${(p) =>
      p.state === 'correct'
        ? theme.GREEN
        : p.state === 'wrong'
          ? theme.SECONDARY
          : p.selected
            ? theme.SECONDARY
            : theme.GREY_30};
  background: ${(p) =>
    p.state === 'correct' ? theme.GREEN : p.state === 'wrong' ? theme.SECONDARY : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 18px;
`;

const ResultBanner = styled.div`
  background: ${(p) => (p.correct ? `${theme.GREEN}15` : `${theme.SECONDARY}10`)};
  border: 1px solid ${(p) => (p.correct ? theme.GREEN : theme.SECONDARY)};
  border-radius: 10px;
  padding: 14px 16px;
  margin: 0 0 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/**
 * QuizPlayModal — Fan-facing UI for answering one quiz.
 *
 * Three phases:
 *   1. Hydrating: GET /quiz/get/:id  to find out if the user already answered.
 *   2. Selecting: user picks an answer, taps Submit → POST /quiz/answer.
 *   3. Result:    show correct answer, the user's pick, and points earned.
 *
 * When the modal opens for an already-answered quiz we skip straight to phase 3
 * by reading `userResponse` + `quiz.answers[].isCorrect` from the /quiz/get response.
 */
const QuizPlayModal = ({ visible, quiz, onClose, onAnswered, requireSignup }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fullQuiz, setFullQuiz] = useState(null); // includes answers with isCorrect (post-submit)
  const [selectedId, setSelectedId] = useState(null);
  const [result, setResult] = useState(null); // { isCorrect, earnedPoints, selectedAnswerId }
  const [err, setErr] = useState(null);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Re-hydrate every time the modal opens for a new quiz. /quiz/get returns
  // either the answered shape (with results) or the unanswered shape (clean answers).
  useEffect(() => {
    if (!visible || !quiz?._id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      setSelectedId(null);
      setResult(null);
      const res = await Api.get(`/quiz/get/${quiz._id}`, null, { silent: true });
      if (cancelled) return;
      if (res?.err) {
        setErr(res.err);
        setLoading(false);
        return;
      }
      setFullQuiz(res?.quiz || null);
      if (res?.answered && res.userResponse) {
        setResult({
          isCorrect: res.userResponse.isCorrect,
          earnedPoints: res.userResponse.earnedPoints,
          selectedAnswerId: res.userResponse.selectedAnswerId
        });
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, quiz?._id]);

  // Mirror the index.js sort: once submitted we want the correct answer's id so
  // we can highlight it green even when the user picked something else.
  const correctAnswerId = useMemo(() => {
    if (!result || !fullQuiz?.answers) return null;
    const correct = fullQuiz.answers.find((a) => a.isCorrect);
    return correct?._id ? String(correct._id) : null;
  }, [result, fullQuiz]);

  const handleSubmit = async () => {
    if (!selectedId || !quiz?._id) return;
    setSubmitting(true);
    setErr(null);
    const res = await Api.post(
      '/quiz/answer',
      { quizId: quiz._id, answerId: selectedId },
      null,
      { silent: true }
    );
    setSubmitting(false);
    if (res?.err) {
      setErr(res.err);
      return;
    }
    // /quiz/answer returns { isCorrect, earnedPoints, breakdown }. Re-fetch the
    // full quiz so we get the answer-correctness map for the result UI.
    const refreshed = await Api.get(`/quiz/get/${quiz._id}`, null, { silent: true });
    if (refreshed?.quiz) setFullQuiz(refreshed.quiz);
    setResult({
      isCorrect: !!res.isCorrect,
      earnedPoints: res.earnedPoints || 0,
      selectedAnswerId: selectedId
    });
    // Surface a toast so the user sees confirmation even if they close the modal fast.
    dispatch({
      type: 'HANDLE_RES',
      payload: {
        res: res.isCorrect
          ? t('pollQuizProfile.toastCorrect')
          : t('pollQuizProfile.toastSubmitted')
      }
    });
    onAnswered?.(quiz._id, { isCorrect: !!res.isCorrect, earnedPoints: res.earnedPoints || 0 });
  };

  if (!visible) return null;

  // Bail out early when an anonymous viewer taps Play — we don't want to expose
  // the quiz copy until they're signed in (and the submit endpoint needs auth).
  if (requireSignup) {
    return (
      <Modal visible={visible} closeModal={onClose} wid="420px !important">
        <Col centerAll pad="32px 24px" gap="12px">
          <Icon type="lock" dimensions={28} col={theme.GREY_60} />
          <Text h3 center>
            {t('pollQuizProfile.signInToPlay')}
          </Text>
          <Text b2 col={theme.GREY_60} center>
            {t('pollQuizProfile.signInToPlaySub')}
          </Text>
          <Button text={t('pollQuizProfile.signUp')} onClick={onClose} col={theme.SECONDARY} marg="8px 0 0" />
        </Col>
      </Modal>
    );
  }

  const renderBody = () => {
    if (loading) {
      return (
        <Col centerAll pad="40px 0">
          <Spinner2 col={theme.SECONDARY} />
        </Col>
      );
    }
    if (err && !fullQuiz) {
      return (
        <Col centerAll pad="24px 0">
          <Text col={theme.SECONDARY}>{err}</Text>
        </Col>
      );
    }
    if (!fullQuiz) return null;

    const answers = fullQuiz.answers || [];

    return (
      <>
        {result && (
          <ResultBanner correct={result.isCorrect}>
            <Text h4 col={result.isCorrect ? theme.GREEN : theme.SECONDARY}>
              {result.isCorrect
                ? t('pollQuizProfile.resultCorrect')
                : t('pollQuizProfile.resultNotQuite')}
            </Text>
            <Text b2 col={theme.GREY_70}>
              {result.earnedPoints > 0
                ? t('pollQuizProfile.earnedPts', { count: result.earnedPoints })
                : t('pollQuizProfile.betterLuck')}
            </Text>
          </ResultBanner>
        )}

        <Text h3 marg="0 0 16px">
          {fullQuiz.question}
        </Text>

        {answers.map((a) => {
          const id = String(a._id);
          const selected = selectedId === id || result?.selectedAnswerId === id;
          let state = null;
          if (result) {
            if (correctAnswerId && id === correctAnswerId) state = 'correct';
            else if (result.selectedAnswerId === id && !result.isCorrect) state = 'wrong';
          }
          return (
            <Option
              key={id}
              type="button"
              selected={selected}
              state={state}
              disabled={!!result || submitting}
              onClick={() => !result && !submitting && setSelectedId(id)}
            >
              <Radio selected={selected} state={state}>
                {state === 'correct' && <Icon type="check" dimensions={10} col="#fff" />}
                {state === 'wrong' && <Icon type="close" dimensions={8} col="#fff" />}
              </Radio>
              <span>{a.text}</span>
            </Option>
          );
        })}

        {err && !result && (
          <Text c2 col={theme.SECONDARY} marg="0 0 12px">
            {err}
          </Text>
        )}

        {!result && (
          <Row between center marg="14px 0 0">
            <Text c2 col={theme.GREY_60}>
              {t('pollQuizProfile.ptsAvailable', { count: fullQuiz.rewardPoints || 0 })}
            </Text>
            <Button
              text={submitting ? t('pollQuizProfile.submitting') : t('pollQuizProfile.submit')}
              onClick={handleSubmit}
              disabled={!selectedId || submitting}
              col={theme.SECONDARY}
              style={{ width: 'fit-content' }}
            />
          </Row>
        )}

        {result && (
          <Row centerAll marg="14px 0 0">
            <Button
              text={t('pollQuizProfile.close')}
              onClick={onClose}
              bordered
              style={{ width: 'fit-content' }}
            />
          </Row>
        )}
      </>
    );
  };

  return (
    <Modal visible={visible} closeModal={onClose} wid="480px !important">
      <Col pad="24px" wid="100%">
        {renderBody()}
      </Col>
    </Modal>
  );
};

export default QuizPlayModal;
